
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import API from "./axiosInstance";

function CheckoutPage() {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);

  // 🎟 Coupon
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    mobile: "",
  });

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const buyNow = JSON.parse(localStorage.getItem("checkoutProduct"));
    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    if (buyNow) {
      const qty = buyNow.quantity || 1;
      setProduct({ ...buyNow, quantity: qty });
      setTotal(buyNow.finalPrice * qty);
    } else {
      setCartItems(cart);
      const sum = cart.reduce(
        (acc, item) => acc + item.finalPrice * item.quantity,
        0
      );
      setTotal(sum);
    }
  }, []);

  /* ================= FINAL TOTAL ================= */
  useEffect(() => {
    setFinalTotal(total - discount);
  }, [total, discount]);

  /* ================= QUANTITY ================= */
  const updateQuantity = (index, delta) => {
    const updated = [...cartItems];
    if (delta === -1 && updated[index].quantity <= 1) return;

    updated[index].quantity += delta;
    setCartItems(updated);
    localStorage.setItem("cart", JSON.stringify(updated));

    const sum = updated.reduce(
      (acc, i) => acc + i.finalPrice * i.quantity,
      0
    );
    setTotal(sum);
    resetCoupon();
  };

  /* ================= REMOVE ITEM ================= */
  const removeItem = (index) => {
    const updated = [...cartItems];
    updated.splice(index, 1);

    setCartItems(updated);
    localStorage.setItem("cart", JSON.stringify(updated));

    const sum = updated.reduce(
      (acc, i) => acc + i.finalPrice * i.quantity,
      0
    );
    setTotal(sum);
    setFinalTotal(sum);
    resetCoupon();
  };

  const resetCoupon = () => {
    setDiscount(0);
    setCouponApplied(false);
    setCouponMsg("");
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  /* ================= APPLY COUPON ================= */
  const applyCoupon = async () => {
    if (!couponCode) return alert("Enter coupon code");

    try {
      const res = await API.post("/coupons/apply", {
        code: couponCode.trim().toUpperCase(),
        orderAmount: total,
      });

      setDiscount(res.data.discount);
      setFinalTotal(res.data.finalAmount);
      setCouponApplied(true);
      setCouponMsg("✅ Coupon applied successfully");
    } catch (err) {
      setCouponMsg(err.response?.data?.message || "Invalid coupon");
      setDiscount(0);
    }
  };

  /* ================= PLACE ORDER ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return alert("Please login first");

    const items = product ? [product] : cartItems;
    if (items.length === 0) return alert("Cart empty");

    const safeItems = items.map((i) => ({
      productId: i.id,
      name: i.name,
      price: i.finalPrice,
      quantity: i.quantity,
      image: i.images?.[0],
    }));

    try {
      setLoading(true);

      await API.post("/orders", {
        email: user.email,
        items: safeItems,
        totalAmount: finalTotal,
        discount,
        couponCode: couponApplied ? couponCode : null,
        ...formData,
      });

      alert("✅ Order placed successfully");
      localStorage.removeItem("cart");
      localStorage.removeItem("checkoutProduct");
      
      navigate("/userdashboard/myorders");
    } catch {
      alert("❌ Order failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 750, margin: "auto", padding: 20 }}>
      <h2>🛒 Checkout</h2>

      {/* CART ITEMS */}
      {!product &&
        cartItems.map((item, index) => (
          <div
            key={item.id}
            style={{
              display: "flex",
              gap: 15,
              border: "1px solid #ddd",
              padding: 12,
              marginBottom: 10,
              alignItems: "center",
            }}
          >
            <img
              src={item.images?.[0] || "https://via.placeholder.com/100"}
              alt={item.name}
              style={{ width: 90, height: 90, objectFit: "cover" }}
            />

            <div style={{ flex: 1 }}>
              <h4>{item.name}</h4>
              <p>₹{item.finalPrice}</p>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button onClick={() => updateQuantity(index, -1)}>-</button>
                <b>{item.quantity}</b>
                <button onClick={() => updateQuantity(index, 1)}>+</button>

                <button
                  onClick={() => removeItem(index)}
                  style={{
                    marginLeft: 15,
                    background: "#fee2e2",
                    color: "#b91c1c",
                    border: "1px solid #fca5a5",
                    cursor: "pointer",
                  }}
                >
                  ❌ Remove
                </button>
              </div>
            </div>

            <b>₹{item.finalPrice * item.quantity}</b>
          </div>
        ))}

      {/* COUPON */}
      {total > 0 && (
        <div style={{ border: "1px dashed green", padding: 10 }}>
          <h4>🎟 Apply Coupon</h4>
          <input
            placeholder="Enter coupon"
            value={couponCode}
            disabled={couponApplied}
            onChange={(e) => setCouponCode(e.target.value)}
          />
          <button onClick={applyCoupon} disabled={couponApplied}>
            Apply
          </button>
          {couponMsg && <p>{couponMsg}</p>}
        </div>
      )}

      {/* TOTAL */}
      {total > 0 && (
        <>
          <h3>Subtotal: ₹{total}</h3>
          {discount > 0 && <h4 style={{ color: "green" }}>Discount: −₹{discount}</h4>}
          <h2 style={{ color: "#16a34a" }}>Payable: ₹{finalTotal}</h2>
        </>
      )}

      {/* ADDRESS */}
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: 20 }}>
        <input name="name" placeholder="Full Name" onChange={handleChange} required />
        <textarea name="address" placeholder="Address" onChange={handleChange} required />
        <input name="city" placeholder="City" onChange={handleChange} required />
        <input name="state" placeholder="State" onChange={handleChange} required />
        <input name="pincode" placeholder="Pincode" onChange={handleChange} required />
        <input name="mobile" placeholder="Mobile" onChange={handleChange} required />

        <button
          disabled={loading}
          style={{ background: "green", color: "#fff", padding: 12 }}
        >
          {loading ? "⏳ Placing Order..." : "✔ Place Order"}
        </button>
      </form>
    </div>
  );
}

export default CheckoutPage;
